import React, { useState, Fragment } from 'react'
import { useForm } from 'react-hook-form'
import { useParams, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { useConfig } from '../../../../routes'

import { Fetch } from '../../../../function/Fetch'

import { Button } from '../../../../components/ui-elements/Button'
import { CheckBox } from '../../../../components/ui-elements/CheckBox'
import { FormProps, FormContainerProps, FormType, ContactItem } from './Form.types'
import {
  TextInputField,
  TextareaField,
  NumberInputField,
  NameInputField,
  SingleSelectField,
  AddressWithPostalCodeField,
  RadioInputField,
  DateInputField,
  MultiSelectField,
  MonthInputField,
} from '../../../../components/ui-parts/Form'
import { TextInput } from '../../../../components/ui-elements/TextInput'

import { format } from 'date-fns'

type ConfigType = {
  contactItems: {
    id: string
    inputType: 'string' | 'select' | 'text' | 'integer'
    isRequired: boolean
    name: string
    order: number
    placeholder: string | null
    selectItemsText: string | null
    itemKey: string
  }[]
  facilityOption: {
    id: string
    name: string
  }[]
  contactSelectItems: string[]
}

export const Form: React.FC<FormContainerProps> = (props) => {
  const config = useConfig() as ConfigType
  const { contactPageKey } = useParams()
  const navigate = useNavigate()

  const {
    register,
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<FormType>({
    defaultValues: {
      facilityId: config.facilityOption.length === 1 ? config.facilityOption[0].id : '',
      contactSelectItemName: config.contactSelectItems.length === 1 ? config.contactSelectItems[0] : '',
      contactInfos: config.contactItems.map((item) => ({ contactItemId: item.id })),
    },
  })
  const onSubmit = handleSubmit(async (data: FormType) => {
    const res = await Fetch(`/customer/api/v1/contact_pages/${contactPageKey}/contacts`, 'POST', {
      contact: {
        facility_id: data.facilityId,
        contact_select_item_name: data.contactSelectItemName,
        last_name: data.lastName,
        first_name: data.firstName,
        last_name_kana: data.lastNameKana,
        first_name_kana: data.firstNameKana,
        email: data.email,
        phone_number: data.phone,
        gender: data.gender,
        postal_code: data.postalCode,
        prefecture: data.prefecture,
        city: data.city,
        house_number: data.houseNumber,
        building_name: data.buildingName,
        content: data.content,
        is_agree_terms: data.agreeTerms,
        contact_infos_attributes: data.contactInfos.map((info) => ({
          contact_item_id: info.contactItemId,
          value: info.value ?? undefined,
          int_value: info.intValue ?? undefined,
          name: info.name ?? undefined,
          values: info.values ?? undefined,
          date_value: info.dateValue ? format(info.dateValue, 'yyyy-MM-dd') : undefined,
        })),
      },
    })

    if (res.ok) {
      navigate(`/customer/public/contact_pages/${contactPageKey}/thanks_contact`)
      return
    } else {
      const errorData = await res.json()
      console.error('Error:', errorData)
    }
  })

  const contactItems: ContactItem[] =
    config.contactItems.map(
      (item): ContactItem => ({
        id: item.id,
        itemKey: item.itemKey,
        inputType: item.inputType,
        required: item.isRequired,
        name: item.name,
        order: item.order || 0,
        placeholder: item.placeholder || '',
        options: String(item.selectItemsText)
          .split(',')
          .map((opt) => ({ value: opt, label: opt })),
      }),
    ) ?? []

  return (
    <StyledComponent
      {...props}
      register={register}
      control={control}
      onSubmit={onSubmit}
      setValue={setValue}
      errors={errors}
      contactItems={contactItems}
      facilityOption={config.facilityOption}
      contactSelectItems={config.contactSelectItems}
    />
  )
}

const Component: React.FC<FormProps> = ({
  className,
  formItemRequirement,
  primaryColor,
  register,
  control,
  onSubmit,
  setValue,
  errors,
  contactItems,
  facilityOption,
  contactSelectItems,
  privacyPolicyLink,
}) => {
  const [isCheckedAgreement, setIsCheckedAgreement] = useState(false)
  const genderOptions = [
    { label: '女性', value: 'female' },
    { label: '男性', value: 'male' },
  ]
  if (formItemRequirement.gender === 'optional') {
    genderOptions.push({ label: 'その他', value: 'other' })
  }

  return (
    <form className={`${className} form`} onSubmit={onSubmit}>
      {facilityOption.length > 1 && (
        <SingleSelectField
          label="ご希望店舗"
          name="facilityId"
          control={control}
          required
          options={facilityOption.map((opt) => ({ value: opt.id, label: opt.name }))}
          error={errors['facilityId']}
        />
      )}
      {contactSelectItems.length > 1 && (
        <SingleSelectField
          label="お問合せ項目"
          name="contactSelectItemName"
          control={control}
          required
          options={contactSelectItems.map((opt) => ({ value: opt, label: opt }))}
          error={errors['contactSelectItemName']}
        />
      )}
      <NameInputField
        label="お名前"
        register={register}
        placeholders={{ lastName: '姓', firstName: '名' }}
        errors={errors}
        required
      />
      {formItemRequirement.kana !== 'none' && (
        <NameInputField
          label="お名前（ふりがな）"
          register={register}
          names={{ lastName: 'lastNameKana', firstName: 'firstNameKana' }}
          placeholders={{ lastName: 'せい', firstName: 'めい' }}
          errors={errors}
          required={formItemRequirement.kana === 'required'}
          pattern="hiragana"
        />
      )}
      <TextInputField<FormType>
        label="メールアドレス"
        name="email"
        type="email"
        pattern="email"
        register={register}
        placeholder="oiwaii@gmail.com"
        error={errors['email']}
        required
      />
      {/* <TextInputField<FormType>
        label="メールアドレス（確認）"
        name="emailConfirmation"
        type="email"
        pattern="email"
        register={register}
        placeholder="oiwaii@gmail.com"
        required
      /> */}
      {formItemRequirement.phoneNumber !== 'none' && (
        <TextInputField<FormType>
          label="電話番号"
          name="phone"
          pattern="phoneNumber"
          register={register}
          placeholder="090-1234-5678"
          error={errors['phone']}
          required={formItemRequirement.phoneNumber === 'required'}
        />
      )}
      {formItemRequirement.gender !== 'none' && (
        <RadioInputField<FormType>
          label="性別"
          name="gender"
          register={register}
          radioOptions={genderOptions}
          error={errors['gender']}
          required={formItemRequirement.gender === 'required'}
        />
      )}
      {formItemRequirement.address !== 'none' && (
        <AddressWithPostalCodeField
          control={control}
          register={register}
          setValue={setValue}
          errors={errors}
          label="住所"
          required={formItemRequirement.address === 'required'}
          color={primaryColor}
        />
      )}
      {formItemRequirement.content !== 'none' && (
        <TextareaField
          label="ご質問・ご要望など"
          name="content"
          register={register}
          placeholder="問い合わせたい内容をご記入ください"
          required={formItemRequirement.content === 'required'}
          error={errors['content']}
          rows={4}
        />
      )}
      {contactItems.map((item, index) => {
        switch (item.inputType) {
          case 'string':
            return (
              <Fragment key={item.id}>
                <TextInput {...register(`contactInfos.${index}.contactItemId`)} type="hidden" value={item.id} />
                <TextInputField<FormType>
                  label={item.name}
                  name={`contactInfos.${index}.value`}
                  register={register}
                  placeholder={item.placeholder}
                  required={item.required}
                  error={errors?.contactInfos?.[index]?.value}
                />
              </Fragment>
            )
          case 'integer':
            return (
              <Fragment key={item.id}>
                <TextInput {...register(`contactInfos.${index}.contactItemId`)} type="hidden" value={item.id} />
                <NumberInputField<FormType>
                  label={item.name}
                  name={`contactInfos.${index}.intValue`}
                  register={register}
                  placeholder={item.placeholder}
                  required={item.required}
                  error={errors?.contactInfos?.[index]?.value}
                />
              </Fragment>
            )
          case 'multi_select':
            return (
              <Fragment key={item.id}>
                <TextInput {...register(`contactInfos.${index}.contactItemId`)} type="hidden" value={item.id} />
                <MultiSelectField
                  label={item.name}
                  name={`contactInfos.${index}.values`}
                  control={control}
                  placeholder={item.placeholder}
                  required={item.required}
                  options={item.options}
                  error={errors?.contactInfos?.[index]?.value}
                />
              </Fragment>
            )
          case 'date':
            return (
              <Fragment key={item.id}>
                <TextInput {...register(`contactInfos.${index}.contactItemId`)} type="hidden" value={item.id} />
                <DateInputField
                  label={item.name}
                  name={`contactInfos.${index}.dateValue`}
                  control={control}
                  placeholder={item.placeholder}
                  required={item.required}
                  error={errors?.contactInfos?.[index]?.value}
                />
              </Fragment>
            )
          case 'month':
            return (
              <Fragment key={item.id}>
                <TextInput {...register(`contactInfos.${index}.contactItemId`)} type="hidden" value={item.id} />
                <MonthInputField
                  label={item.name}
                  name={`contactInfos.${index}.dateValue`}
                  control={control}
                  placeholder={item.placeholder}
                  required={item.required}
                  error={errors?.contactInfos?.[index]?.value}
                />
              </Fragment>
            )
          case 'text':
            return (
              <Fragment key={item.id}>
                <TextInput {...register(`contactInfos.${index}.contactItemId`)} type="hidden" value={item.id} />
                <TextareaField<FormType>
                  label={item.name}
                  name={`contactInfos.${index}.value`}
                  register={register}
                  placeholder={item.placeholder}
                  required={item.required}
                  error={errors?.contactInfos?.[index]?.value}
                  rows={4}
                />
              </Fragment>
            )
          case 'select':
            return (
              <Fragment key={item.id}>
                <TextInput {...register(`contactInfos.${index}.contactItemId`)} type="hidden" value={item.id} />
                <SingleSelectField
                  label={item.name}
                  name={`contactInfos.${index}.value`}
                  control={control}
                  placeholder={item.placeholder}
                  required={item.required}
                  options={item.options}
                  error={errors?.contactInfos?.[index]?.value}
                />
              </Fragment>
            )
          default:
            return <Fragment key={item.id} />
        }
      })}
      <div className={`${className} agreement`}>
        <CheckBox
          linkText="プライバシーポリシー"
          linkURL={privacyPolicyLink}
          label={`${privacyPolicyLink ? '' : 'プライバシーポリシー'}に同意する`}
          required
          checked={isCheckedAgreement}
          onChange={(e) => setIsCheckedAgreement(e.target.checked)}
        />
      </div>
      <div className="submit">
        <Button type="submit" color={primaryColor} disabled={!isCheckedAgreement}>
          送信する
        </Button>
      </div>
    </form>
  )
}

const StyledComponent = styled(Component)`
  &.form {
    /* NOTE: gridを使うと〇〇Fieldのlabelとinputが何故か折り返してしまうためflexとする */
    display: flex;
    flex-direction: column;
    gap: 24px;

    @media (max-width: 751px) {
      gap: 12px;
    }
  }
  &.agreement {
    margin: 0 auto;
  }
`
